import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  useEnrollUserInCourseMutation,
  useMakeCoursePaymentMutation,
  Course,
  GetEnrolledCoursesForStudentDocument,
  GetAvailableCoursesForStudentDocument,
} from "../graphql/generated/graphql";
import { Animated, Platform } from "react-native";
import handleOpenLink from "../utils/handleOpenLink";
import { useAppConfig } from "../AppConfigProvider";

type CourseDetailsContextType = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  courseData: Partial<Course>;
  setCourseData: (courseData: Partial<Course>) => void;
  onEnroll: (id: string, hasCost: boolean, callback?: () => void) => Promise<void>;
  loading: boolean;
  slideAnim: Animated.Value;
};

const defaultCourseData: Partial<Course & { preRequisitesCompleted: boolean }> = {
  courseId: "",
  name: "",
  description: "",
  objectives: [],
  targetAudience: "",
  modules: [],
  cost: 0,
  preRequisitesCompleted: false,
  preRequisites: [],
};

const CourseDetailsContext = createContext<CourseDetailsContextType>({
  isVisible: false,
  setIsVisible: () => {},
  courseData: defaultCourseData,
  setCourseData: () => {},
  onEnroll: () => Promise.resolve(),
  loading: false,
  slideAnim: new Animated.Value(0),
});

const CourseDetailsProvider = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [courseData, setCourseData] = useState(defaultCourseData);
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const courseDetailsSlideAnim = useRef(new Animated.Value(270)).current;

  useEffect(() => {
    if (isVisible) {
      Animated.timing(courseDetailsSlideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(courseDetailsSlideAnim, {
        toValue: 270,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [isVisible, courseDetailsSlideAnim]);

  const [createCheckoutSession, { loading: creatingPaymentSession }] = useMakeCoursePaymentMutation();
  const [enrollStudent, { loading: enrollingUserInCourse }] = useEnrollUserInCourseMutation({
    refetchQueries: [
      {
        query: GetEnrolledCoursesForStudentDocument,
        variables: {
          schoolId,
        },
      },
      {
        query: GetAvailableCoursesForStudentDocument,
        variables: {
          schoolId,
        },
      },
    ],
  });

  const onEnroll = async (id, hasCost, callback) => {
    try {
      if (hasCost) {
        const response = await createCheckoutSession({
          variables: {
            courseId: id,
          },
        });

        const stripeUrl = response?.data?.createCheckoutSession?.url;

        if (!stripeUrl) {
          console.error("No Stripe URL returned from checkout session");
          throw new Error("Payment processing failed - no checkout URL");
        }

        if (Platform.OS === "web") {
          window.open(stripeUrl, "_blank");
        } else {
          await handleOpenLink(stripeUrl);
        }
      } else {
        await enrollStudent({
          variables: {
            courseId: id,
          },
        });
      }

      setIsVisible(false);
      setCourseData(defaultCourseData);

      if (typeof callback === "function") {
        callback();
      }
    } catch (error) {
      console.error("Enrollment error:", error);
    }
  };

  const loading = creatingPaymentSession || enrollingUserInCourse;

  return (
    <CourseDetailsContext.Provider
      value={{
        isVisible,
        setIsVisible,
        courseData,
        setCourseData,
        onEnroll,
        loading,
        slideAnim: courseDetailsSlideAnim,
      }}>
      {children}
    </CourseDetailsContext.Provider>
  );
};

const useCourseDetails = () => {
  const context = useContext(CourseDetailsContext);
  if (!context) {
    throw new Error("useCourseDetails must be used within a CourseDetailsProvider");
  }
  return context;
};

export { CourseDetailsProvider, useCourseDetails };
